import { useMemo, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { checkSpanishLocale, extractLabelAndComment } from 'lib/utils';
import { getSymptomQuestion } from 'services/symptom-checker/api';
import {
  useGetSymptomQuestion,
  usePostSymptomQuestion,
} from 'services/symptom-checker/hooks';
import { SymptomQuestion } from 'services/symptom-checker/type';
import useAuthStore from 'store/auth';
// import { useSymptomStore } from 'store/symptom';
import { ScrollArea } from 'components/ui/scroll-area';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { PATH_ROUTES } from 'routes/path';
import Loader from 'components/Loader';
import { useSymptomStore } from 'store/symptom';

export default function SymptomCheckerReview() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const profileId = useAuthStore((store) => store.currentUserProfileId);
  // const questionData = useSymptomStore((store) => store.questionData)?.data;

  const [sendEmail, setSendEmail] = useState('true');

  const { data: reviewData, isFetching } = useGetSymptomQuestion({
    payload: { type: 'latest', profileId, restart_flag: 'false' },
  });

  const { mutate: postQuestion, isPending } = usePostSymptomQuestion({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['symptom-checker-reports'] });
      // navigation.dispatch(
      //   StackActions.replace(
      //     ROUTES.MAIN.GENERATE_SYMPTOM_CHECKER.GENERATING,
      //     data.data
      //   )
      // );
      navigate(PATH_ROUTES.GENERATE_SYMPTOM_GENERATING);
    },
    onError: () => {
      // errorToast();
      toast.error(t('failed_to_generate_report'));
    },
  });

  if (isFetching) {
    return <Loader />;
  }

  const onSendChange = () => {
    setSendEmail((prev) => (prev === 'true' ? 'false' : 'true'));
  };

  const onGenerate = () => {
    postQuestion({
      type: 'review',
      email_flag: sendEmail,
    });
  };

  return (
    <div className='px-6 py-4 space-y-4'>
      <h1 className='text-xl font-semibold'>{t('review_symptom_title')}</h1>
      <ScrollArea className='lg:h-[calc(100vh-15rem)]'>
        <div className='space-y-4'>
          {reviewData?.data && Array.isArray(reviewData?.data)
            ? reviewData?.data?.map((item, index) => (
                <div
                  key={item.q_id}
                  className='p-4 pt-3 space-y-2 bg-primary/10 rounded-2xl'
                >
                  <ReviewQuestion data={item} index={index} />
                  <ReviewAnswer data={item} />
                </div>
              ))
            : null}
        </div>
        <div className='flex items-center justify-between p-4 pt-3 mt-4 space-y-2 bg-primary/10 rounded-2xl'>
          <p className='pt-2 text-sm font-semibold md:text-base md:pt-0 md:leading-8'>
            {t('sen_symptom_report_email')}
          </p>
          <Switch
            checked={sendEmail === 'true'}
            onCheckedChange={onSendChange}
          />
        </div>
      </ScrollArea>

      <div className='flex justify-center'>
        <Button className='px-6' onClick={onGenerate} disabled={isPending}>
          {isPending && (
            <Loader2 role='progressbar' className='mr-1 animate-spin size-4' />
          )}
          {t('generate_report')}
        </Button>
      </div>
    </div>
  );
}

function ReviewQuestion({
  data,
  index,
}: {
  data: SymptomQuestion;
  index: number;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!data) {
    return null;
  }
  const isSpanish = checkSpanishLocale();

  const setQuestionData = useSymptomStore((store) => store.setQuestionData);

  const { label } = extractLabelAndComment(
    data?.[isSpanish ? 'spanish_question' : 'eng_question'] ?? ''
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onEdit = async () => {
    try {
      setIsLoading(true);
      const response = await getSymptomQuestion({
        type: 'single_question',
        q_id: data.q_id,
      });
      console.log('🚀 ~ onEdit ~ response:', response);

      if (response?.data?.q_id) {
        setQuestionData(response);
        navigate(
          `${PATH_ROUTES.GENERATE_SYMPTOM_CHECKER}?code=${response.data.code}`
        );
      }
      // const nextRoute = SymptomCodeScreenMapper[res.data.code];
      // navigation.navigate(nextRoute, {
      //   ...res.data,
      //   code: res.data.code,
      //   isEdit: true,
      // });
    } catch (error) {
      // errorToast();
      toast.error(t('failed_to_get_question'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex items-center justify-between'>
      <div className='flex items-center gap-3'>
        <div className='grid bg-white rounded-full size-6 place-content-center shrink-0'>
          <p className='text-sm font-medium'>{index + 1}</p>
        </div>
        <p className='text-sm font-semibold leading-tight md:text-base'>
          {label}
        </p>
      </div>
      <button
        className='grid bg-white rounded-full size-7 place-content-center shrink-0'
        onClick={onEdit}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className='text-gray-400 animate-spin' size={16} />
        ) : (
          <Pencil className='size-4 text-primary' />
        )}
      </button>
    </div>
  );
}

function ReviewAnswer({ data }: { data: SymptomQuestion }) {
  const isSpanish = checkSpanishLocale();
  const metaData = data?.meta_data?.type;
  const isFileUpload = metaData === 'image_upload';
  const answer =
    data?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];

  const parsedAnswer = useMemo(() => {
    if (!answer) {
      return '';
    }

    let parsedAns = answer;
    try {
      const parsed = JSON.parse(answer);
      if (Array.isArray(parsed)) {
        const formattedAnswer: string[] = [];
        parsed.forEach((item: any) => {
          if (typeof item === 'string') {
            formattedAnswer.push(item);
          } else {
            const entry = Object.entries(item);
            if (entry?.length) {
              formattedAnswer.push(`${entry[0][0]}: ${entry[0][1]}`);
            }
          }
        });
        parsedAns = formattedAnswer;
      } else {
        const entry = Object.entries(parsed);
        if (entry?.length) {
          parsedAns = `${entry[0][0]}: ${entry[0][1]}`;
        }
      }
    } catch (error) {
      const { label, comment } = extractLabelAndComment(answer);
      parsedAns = `${label}${comment ? ` -${comment}` : ''}`;
    }

    return parsedAns;
  }, [answer]);

  if (!data) {
    return null;
  }

  if (isFileUpload) {
    return (
      <div className='px-4 py-2 bg-white rounded-xl min-h-10'>
        <div>
          {answer.map((image: { id: string; url: string }) => (
            <img
              key={image.id}
              src={image.url}
              alt={image.id}
              className='object-cover w-24 h-24 rounded-lg'
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='px-4 py-2 bg-white rounded-xl min-h-10'>
      {Array.isArray(parsedAnswer) ? (
        <div className='flex flex-wrap items-center gap-2 '>
          {parsedAnswer.map((item, index) => (
            <div
              key={index}
              className='px-2 py-1 border border-primary rounded-xl'
            >
              <p className='text-sm font-medium'>{item}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className='text-sm font-medium'>{parsedAnswer}</p>
      )}
    </div>
  );
}
